import React, { useState, useEffect, useCallback, useRef } from "react";
import {
  Modal,
  Input,
  Button,
  Select,
  message,
  Table,
  Spin,
  Space
} from "antd";
import { openAuthPopup } from "../utils/popupManager";
import { useNavigate } from "react-router-dom";
import {
  MailOutlined
} from "@ant-design/icons";
import axios from "axios";
import axiosAuth from "../utils/axiosAuth";
import Sidebar from "./Sidebar";
import { getAuth } from "firebase/auth";
import { useAuth } from "../context/AuthContext";
import { db } from "../firebase/firebase";
import {
  deleteDoc,
  doc
} from "firebase/firestore";
import "./styles/CreateCampaign.css";
import countryList from "react-select-country-list";
import { generateSpintaxVariations, detectSpintax, convertToSpintax } from "../core/spintax-v2/engine";
// PHASE 1: Import shared Gmail accounts hook (eliminates duplicate API calls)
import { useGmailAccounts } from "../hooks/useGmailAccounts";
// PHASE 2B: Import unified campaign data hooks
import { useCampaignsList, useInvalidateCampaigns, useCampaignDataInvalidation } from "../hooks/useCampaignData";
// ⚡ Import optimistic update helper for instant post-payment UX
import { initGlobalOptimisticUpdate } from "../hooks/useCampaignDataOptimistic";
import { useQueryClient } from '@tanstack/react-query';
// ✅ NEW: Import subscription hook for subscription status detection
import { useSubscription } from "../hooks/useSubscription";
import AdvancedCampaignSettings from "./AdvancedCampaignSettings";
import AdvancedCampaignContentFields from "./AdvancedCampaignContentFields";
import {
  DEFAULT_WEEKDAYS,
  DEFAULT_SENDING_FULL_DAY,
  DEFAULT_SENDING_START_TIME,
  DEFAULT_SENDING_END_TIME,
  createNewCampaignScheduleDefaults,
  getBrowserTimezone,
  isAdvancedAccountAvailable,
  isAdvancedCampaignCapabilityEnabled,
  resolvePersistedCampaignSchedule,
  validateAdvancedCampaignForm,
  validateAdvancedCampaignContentForm
} from "../utils/advancedCampaignForm";

// Correct CSS import for Ant Design v4.x
import 'antd/dist/antd.min.css';

const { TextArea } = Input;
const API_BASE_URL = process.env.REACT_APP_API_URL || "https://api.fans.nextremedy.net";

const allCountries = countryList().getData();
// ✅ FIX: "Global" should be FIRST OPTION + placeholder (matches MEO exactly)
const countryOptions = [
  { name: "Global", code: "" }, // ✅ First option in list (matches MEO)
  ...allCountries.map((country) => ({
    name: country.label,
    code: country.value
  }))
];

/**
 * EVENT-DRIVEN CAMPAIGN REFRESH SYSTEM
 * 
 * Replaces polling with event-driven refreshes.
 * Campaigns refresh ONLY when:
 * 1. Component mounts (initial load)
 * 2. Campaign is created successfully
 * 3. Custom 'refreshCampaigns' event is dispatched
 * 
 * Usage: window.triggerCampaignRefresh('source')
 */
window.triggerCampaignRefresh = (source = 'manual') => {
  console.log(`🔄 Triggering campaign refresh from: ${source}`);
  window.dispatchEvent(new CustomEvent('refreshCampaigns', { 
    detail: { source } 
  }));
};

// ✅ Component for New Daily Quota - unified quota tracking system
const NewDailyQuota = ({ dailyEmailsSent, gmailAccount }) => {
  const emailsSentToday = typeof dailyEmailsSent === 'number' ? dailyEmailsSent : 0;
  const maxQuota = 40;
  const remaining = Math.max(0, maxQuota - emailsSentToday);
  const percentage = Math.round((emailsSentToday / maxQuota) * 100);

  let quotaColor = "#52c41a";
  if (percentage >= 80) quotaColor = "#faad14";
  if (percentage >= 100) quotaColor = "#ff4d4f";

  return (
    <div style={{ minHeight: '40px', padding: '2px' }}>
      <div style={{ color: quotaColor, fontWeight: "bold" }}>
        {emailsSentToday} / {maxQuota}
      </div>
      <div style={{ fontSize: "11px", color: "#666" }}>
        {remaining} remaining today
      </div>
      {gmailAccount && gmailAccount !== 'N/A' && (
        <div style={{ fontSize: "10px", color: "#999" }}>
          {gmailAccount}
        </div>
      )}
    </div>
  );
};

// ✅ Component for Progress cell - uses backend-calculated data
const ProgressInfo = ({ totalSent, totalRecipients }) => {
  const sent = totalSent || 0;
  const total = totalRecipients || 0;
  const progressPercent = total > 0 ? Math.round((sent / total) * 100) : 0;
  
  return (
    <div>
      <div style={{ color: '#52c41a', fontSize: '14px', fontWeight: '500' }}>
        {sent} / {total}
      </div>
      <div style={{ fontSize: '11px', color: '#666' }}>
        {progressPercent}% complete
      </div>
      <div style={{ color: '#52c41a', fontSize: '12px' }}>✅</div>
    </div>
  );
};

const CreateCampaign = () => {
  // Auth context
  const { user } = useAuth();
  
  // PHASE 2B: Use unified campaign data hooks for instant page loads
  const { 
    data: cachedCampaigns, 
    isLoading: isCampaignsLoading, 
    error: campaignsFetchError,
    refetch: refetchCampaigns
  } = useCampaignsList(user?.uid, { enabled: !!user });
  
  // PHASE 2B: Cache invalidation hook
  const invalidateCampaigns = useInvalidateCampaigns();
  
  // ✅ NEW: Load subscription status to determine payment flow
  const { data: subscription, isLoading: subscriptionLoading } = useSubscription(user?.uid);
  const isSubscribed = subscription?.status === 'active';
  
  // ⚡ EVENT-DRIVEN INVALIDATION: Auto-invalidate cache on events (mirrors MEO pattern)
  useCampaignDataInvalidation(user?.uid);
  
  // ⚡ Initialize optimistic update helper for instant post-payment UX
  const queryClient = useQueryClient();
  useEffect(() => {
    initGlobalOptimisticUpdate(queryClient);
  }, [queryClient]);
  
  // 🧪 TIMING TEST: Verify post-payment UI updates
  useEffect(() => {
    // Check if we just came from a payment
    const timingData = sessionStorage.getItem('afc_payment_timing');
    if (timingData && cachedCampaigns) {
      try {
        const { startTime, campaignId, expectedState } = JSON.parse(timingData);
        const totalTime = performance.now() - startTime;
        
        console.log('\n🧪 ========================================');
        console.log(`🧪 [TimingTest] Redirected to /campaigns at ${totalTime.toFixed(2)}ms`);
        
        // Find the campaign in the loaded data
        const updatedCampaign = cachedCampaigns.find(c => c.id === campaignId);
        
        if (updatedCampaign) {
          console.timeEnd('[AFC Timing] postPaymentRenderTime');
          
          // Verify each expected field
          const checks = {
            paymentStatus: updatedCampaign.paymentStatus === expectedState.paymentStatus,
            status: updatedCampaign.status === expectedState.status,
            recipients: (updatedCampaign.totalRecipients || 0) === 0
          };
          
          console.log('🧪 [TimingTest] Payment Status =', updatedCampaign.paymentStatus);
          console.log('🧪 [TimingTest] Status =', updatedCampaign.status);
          console.log('🧪 [TimingTest] Recipients =', updatedCampaign.totalRecipients || 0);
          
          if (checks.paymentStatus) {
            console.log('✅ [TimingTest] Payment Status = Paid');
          } else {
            console.log('❌ [TimingTest] Payment Status NOT updated (expected: paid, got:', updatedCampaign.paymentStatus + ')');
          }
          
          if (updatedCampaign.paymentStatus === 'paid') {
            console.log('✅ [TimingTest] Pay Now = Hidden');
          } else {
            console.log('❌ [TimingTest] Pay Now button still visible');
          }
          
          if (checks.status) {
            console.log('✅ [TimingTest] Status = Awaiting Recipients');
          } else {
            console.log('❌ [TimingTest] Status NOT updated (expected: pending, got:', updatedCampaign.status + ')');
          }
          
          if (checks.recipients) {
            console.log('✅ [TimingTest] Recipients = Waiting for recipients... (amber)');
          } else {
            console.log('❌ [TimingTest] Recipients field has unexpected value:', updatedCampaign.totalRecipients);
          }
          
          const allPassed = Object.values(checks).every(c => c);
          
          if (allPassed && totalTime < 1000) {
            console.log(`\n✅ [TimingTest] Post-payment timing test PASSED in ${totalTime.toFixed(0)}ms`);
            console.log('🧪 ========================================\n');
          } else if (!allPassed) {
            console.log(`\n❌ [TimingTest] Post-payment timing test FAILED - Some fields not updated correctly`);
            console.log('🧪 ========================================\n');
          } else {
            console.log(`\n⚠️ [TimingTest] Post-payment timing test SLOW - took ${totalTime.toFixed(0)}ms (expected <1000ms)`);
            console.log('🧪 ========================================\n');
          }
        } else {
          console.log('❌ [TimingTest] Campaign not found in loaded data');
          console.log('🧪 ========================================\n');
        }
        
        // Clear the timing data so it doesn't run again
        sessionStorage.removeItem('afc_payment_timing');
        
      } catch (error) {
        console.error('❌ [TimingTest] Error parsing timing data');
      }
    }
  }, [cachedCampaigns]);
  
  // Modal and editing state
  const [showModal, setShowModal] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editingCampaignId, setEditingCampaignId] = useState(null);
  const [editingCampaign, setEditingCampaign] = useState(null); // PATCH: Store full campaign for recipients display
  
  // Form fields
  const [emailSubject, setEmailSubject] = useState("");
  const [searchTermInput, setSearchTermInput] = useState("");
  const [emailBody, setEmailBody] = useState("");
  const [targetCountry, setTargetCountry] = useState(undefined); // ✅ undefined shows placeholder (MEO pattern)
  const [gmailAccount, setGmailAccount] = useState("");
  const [selectedGmailAccounts, setSelectedGmailAccounts] = useState([]);
  const [sendingWeekdays, setSendingWeekdays] = useState(() => [...DEFAULT_WEEKDAYS]);
  const [sendingFullDay, setSendingFullDay] = useState(DEFAULT_SENDING_FULL_DAY);
  const [sendingStartTime, setSendingStartTime] = useState(DEFAULT_SENDING_START_TIME);
  const [sendingEndTime, setSendingEndTime] = useState(DEFAULT_SENDING_END_TIME);
  const [campaignTimezone, setCampaignTimezone] = useState(getBrowserTimezone());
  const [contactTier, setContactTier] = useState("500"); // ✅ Pre-select first option (500 contacts)
  const [subscriptionPlan, setSubscriptionPlan] = useState("");
  const [contentVariations, setContentVariations] = useState([]);
  const [showSpintaxPreview, setShowSpintaxPreview] = useState(false);
  const [spintaxPreview, setSpintaxPreview] = useState([]);
  
  // UI state
  const [campaigns, setCampaigns] = useState([]);
  const [gmailAccounts, setGmailAccounts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const saveInFlightRef = useRef(false);
  
  const navigate = useNavigate();

  // Reset form to initial state
  const resetForm = () => {
    const scheduleDefaults = createNewCampaignScheduleDefaults();
    setEmailSubject("");
    setSearchTermInput("");
    setEmailBody("");
    setTargetCountry(undefined); // ✅ FIX: Use undefined to show placeholder, not ""
    setGmailAccount("");
    setSelectedGmailAccounts([]);
    setSendingWeekdays(scheduleDefaults.sendingWeekdays);
    setSendingFullDay(scheduleDefaults.sendingFullDay);
    setSendingStartTime(scheduleDefaults.sendingStartTime);
    setSendingEndTime(scheduleDefaults.sendingEndTime);
    setCampaignTimezone(scheduleDefaults.campaignTimezone);
    setContactTier(isSubscribed ? "1000" : "500"); // ✅ Match MEO: subscribers default to 1,000 contacts
    setSubscriptionPlan("");
    setContentVariations([]);
    setShowSpintaxPreview(false);
    setError("");
    setIsEditing(false);
    setEditingCampaignId(null);
    setEditingCampaign(null); // PATCH: Clear editing campaign
  };

  // PHASE 1 OPTIMIZATION: Use shared Gmail accounts hook instead of duplicate API call
  // This eliminates redundant /api/accounts-v2/current requests (was also called in ManageAccounts)
  const { 
    data: gmailAccountsData, 
    isLoading: gmailAccountsLoading,
    refetch: refetchGmailAccounts,
    error: gmailAccountsError 
  } = useGmailAccounts(user?.uid, { enabled: !!user });
  const canUseAdvancedCampaignSending =
    isAdvancedCampaignCapabilityEnabled(gmailAccountsData);

  // Update local state when hook data changes
  useEffect(() => {
    if (gmailAccountsData?.accounts) {
      const accounts = gmailAccountsData.accounts;
      setGmailAccounts(accounts);
      console.log("✅ [CreateCampaign] Gmail v2 - Accounts loaded from shared hook:", accounts.length);
      
      // ✅ AUTO-SELECTION: Select first account if none selected
      if (!gmailAccount && accounts.length > 0) {
        setGmailAccount(accounts[0].email);
        console.log("🔍 [CreateCampaign] Auto-selected first authorised Gmail account");
      }
      if (canUseAdvancedCampaignSending && selectedGmailAccounts.length === 0) {
        const firstAvailable = accounts.find(isAdvancedAccountAvailable);
        if (firstAvailable) {
          setSelectedGmailAccounts([firstAvailable.email]);
          setGmailAccount(firstAvailable.email);
        }
      }
    }
  }, [
    gmailAccountsData,
    gmailAccount,
    canUseAdvancedCampaignSending,
    selectedGmailAccounts.length
  ]);

  // Handle Gmail accounts loading errors
  useEffect(() => {
    if (gmailAccountsError) {
      console.error("❌ [CreateCampaign] Gmail v2 - Error fetching accounts", { status: gmailAccountsError?.response?.status || null });
      if (gmailAccountsError.response?.status === 401) {
        message.error("Unauthorized: Please log in again.");
      } else if (gmailAccountsError.code === 'NETWORK_ERROR' || !gmailAccountsError.response) {
        message.error("Network error: Unable to connect to server.");
      } else {
        message.warning("Failed to load Gmail v2 accounts. Please add a Gmail account first.");
      }
    }
  }, [gmailAccountsError]);

  // ✅ Subscription parity: default to 1,000 contacts when subscribed (matches MEO)
  useEffect(() => {
    if (!showModal || subscriptionLoading) {
      return;
    }

    if (isSubscribed && !isEditing) {
      if (contactTier !== '1000') {
        setContactTier('1000');
      }
      if (subscriptionPlan) {
        setSubscriptionPlan('');
      }
    }
  }, [isSubscribed, showModal, isEditing, subscriptionLoading, contactTier, subscriptionPlan]);

  const subscriptionSelectValue = isSubscribed ? 'already_subscribed' : subscriptionPlan;

  // ✅ EVENT-DRIVEN REFRESH SYSTEM (replaces polling)
  // Set up event listener for campaign refreshes
  useEffect(() => {
    // ✅ Event-driven refresh - listen for custom 'refreshCampaigns' events
    const handleRefreshEvent = (event) => {
      const source = event.detail?.source || 'unknown';
      console.log(`🔄 [CampaignRefresh] Triggered by: ${source}`);
      
      // Invalidate all campaign caches (full, summary, aggregate) for comprehensive refresh
      // This ensures all pages (Dashboard, Analytics, Campaigns) see updated data
      if (user?.uid) {
        invalidateCampaigns(user.uid);
        console.log(`✅ [CampaignRefresh] All authenticated caches invalidated`);
      } else {
        console.warn(`⚠️ [CampaignRefresh] No user ID, falling back to refetch only`);
        refetchCampaigns(); // Fallback if user not available
      }
    };
    
    window.addEventListener('refreshCampaigns', handleRefreshEvent);
    
    // Cleanup event listener on unmount
    return () => {
      window.removeEventListener('refreshCampaigns', handleRefreshEvent);
    };
  }, [user?.uid, invalidateCampaigns, refetchCampaigns]);

  // ✅ ChatGPT Landing: Auto-open modal with pre-fill from chat
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('openModal') === 'true') {
      // Get pre-fill data from sessionStorage
      const preFillData = sessionStorage.getItem('preFillCampaign');
      if (preFillData) {
        try {
          const { searchTerm, emailSubject, emailBody } = JSON.parse(preFillData);
          
          console.log('🎨 [ChatGPT] Loading campaign pre-fill data', {
            hasSearchTerm: !!searchTerm,
            hasEmailSubject: !!emailSubject,
            hasEmailBody: !!emailBody
          });
          
          // Pre-fill form fields using AFC's state setters
          setEmailSubject(emailSubject);
          setEmailBody(emailBody);
          setSearchTermInput(searchTerm); // Note: AFC uses searchTermInput, not searchTerm
          setContactTier('500'); // Default tier
          
          // Open modal
          setShowModal(true);
          
          message.success('Campaign idea loaded! Complete the details below.');
          
          // Clear session storage
          sessionStorage.removeItem('preFillCampaign');
          
        } catch (e) {
          console.error('Failed to load pre-fill data');
          message.error('Failed to load campaign idea');
        }
      } else {
        // No pre-fill data but modal requested - just open it
        setShowModal(true);
      }
      
      // Clean URL (remove query param)
      window.history.replaceState({}, '', '/campaigns');
    }
  }, []); // Run once on mount

  // ✅ Gmail v2 - Handle popup messages (PHASE 1: use shared hook refetch)
  useEffect(() => {
    const handleMessage = (event) => {
      console.log("📥 Gmail v2 - Popup completion message received");
      
      if (event.data === "gmail_v2_account_added") {
        console.log("✅ Gmail v2 - Account added successfully, refreshing via shared hook...");
        refetchGmailAccounts().then(() => {
          message.success("✅ Gmail account added successfully!");
        }).catch(err => {
          console.error("❌ Failed to refresh Gmail accounts");
          message.error("Account added but failed to refresh list. Please try again.");
        });
      } else if (event.data === "gmail_v2_account_add_error") {
        console.log("❌ Gmail v2 - Account add failed");
        message.error("❌ Failed to add Gmail account. Please try again.");
      }
    };

    window.addEventListener("message", handleMessage);
    return () => {
      window.removeEventListener("message", handleMessage);
    };
  }, [refetchGmailAccounts]); // PHASE 1: Use shared hook refetch

  // ✅ PERFORMANCE: Update campaigns state when cached data changes
  useEffect(() => {
    if (cachedCampaigns) {
      setCampaigns(cachedCampaigns);
      setLoading(false);
      console.log(`📦 [Cache] Loaded ${cachedCampaigns.length} campaigns from cache`);
    }
  }, [cachedCampaigns]);

  // ✅ PERFORMANCE: Handle loading state from React Query
  useEffect(() => {
    setLoading(isCampaignsLoading);
  }, [isCampaignsLoading]);

  // ✅ Handle fetch errors
  useEffect(() => {
    if (campaignsFetchError) {
      console.error("Error fetching campaigns", { status: campaignsFetchError?.response?.status || null });
      message.error("Failed to load campaigns");
      setError(campaignsFetchError.message);
    }
  }, [campaignsFetchError]);

  const validateGmailAccount = () => {
    if (canUseAdvancedCampaignSending) {
      const validation = validateAdvancedCampaignForm({
        gmailAccounts: selectedGmailAccounts,
        sendingWeekdays,
        fullDay: sendingFullDay,
        startTime: sendingStartTime,
        endTime: sendingEndTime,
        timezone: campaignTimezone
      });
      if (!validation.valid) {
        setError(validation.error);
        return false;
      }
      const selectedAccountsAreAvailable = selectedGmailAccounts.every((email) => {
        const account = gmailAccounts.find(
          (candidate) => candidate.email.trim().toLowerCase() === email.trim().toLowerCase()
        );
        return isAdvancedAccountAvailable(account);
      });
      if (!selectedAccountsAreAvailable) {
        setError("Every selected Gmail account must still be connected and authorised.");
        return false;
      }
      setError("");
      return true;
    }

    if (!gmailAccount) {
      setError("Please select a Gmail account");
      return false;
    }
    setError("");
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (saveInFlightRef.current) return;
    if (!validateGmailAccount()) return;

    const user = getAuth().currentUser;
    if (!user) {
      message.error("You must be logged in to create a campaign.");
      return;
    }

    const contentValidation = validateAdvancedCampaignContentForm({
      emailSubject,
      emailBody
    });
    if (!contentValidation.valid) {
      setError(contentValidation.error);
      return;
    }

    const advancedValidation = canUseAdvancedCampaignSending
      ? validateAdvancedCampaignForm({
          gmailAccounts: selectedGmailAccounts,
          sendingWeekdays,
          fullDay: sendingFullDay,
          startTime: sendingStartTime,
          endTime: sendingEndTime,
          timezone: campaignTimezone
        })
      : null;

    if (isEditing && editingCampaignId) {
      if (canUseAdvancedCampaignSending && editingCampaign?.advancedSendingEnabled === true) {
        saveInFlightRef.current = true;
        try {
          setLoading(true);
          setError("");
          const idToken = await user.getIdToken();
          const response = await axios.patch(
            `${API_BASE_URL}/api/campaigns/${editingCampaignId}/advanced-sending`,
            {
              gmailAccounts: advancedValidation.value.gmailAccounts,
              sendingWeekdays: advancedValidation.value.sendingWeekdays,
              sendingWindow: advancedValidation.value.sendingWindow,
              emailSubject: contentValidation.value.emailSubject,
              emailBody: contentValidation.value.emailBody
            },
            {
              headers: {
                Authorization: `Bearer ${idToken}`,
                "Content-Type": "application/json"
              }
            }
          );
          if (response.data.success) {
            const persisted = response.data.advancedSending;
            setEmailSubject(persisted.emailSubject);
            setEmailBody(persisted.emailBody);
            if (persisted.contentVariationsReset) {
              setContentVariations([]);
            }
            setEditingCampaign((current) => ({
              ...current,
              gmailAccount: persisted.gmailAccounts?.[0] || current?.gmailAccount,
              gmailAccounts: persisted.gmailAccounts,
              sendingWeekdays: persisted.sendingWeekdays,
              sendingWindow: persisted.sendingWindow,
              emailSubject: persisted.emailSubject,
              emailBody: persisted.emailBody,
              contentRevision: persisted.contentRevision,
              contentVariations: persisted.contentVariationsReset
                ? []
                : current?.contentVariations
            }));
            message.success("Campaign changes saved.");
            invalidateCampaigns(user.uid);
            window.triggerCampaignRefresh?.('advanced-settings-updated');
          }
        } catch (updateError) {
          setError(updateError.response?.data?.error || "Failed to save campaign changes.");
        } finally {
          saveInFlightRef.current = false;
          setLoading(false);
        }
        return;
      }
      message.info("This campaign’s existing details cannot be changed.");
      return;
    }

    if (!searchTermInput.trim()) {
      setError("Search term is required");
      return;
    }
    if (!contactTier && !subscriptionPlan) {
      setError("Please select either a contact tier or subscription plan");
      return;
    }
  
    // Calculate price with new tiers + country add-on
    let price = 0;
    if (subscriptionPlan) {
      const subscriptionPrices = {
        'starter': 99,
        'growth': 299,
        'scale': 599,
        'Starter': 99,
        'Growth': 299,
        'Scale': 599
      };
      price = subscriptionPrices[subscriptionPlan] || 0;
    } else if (contactTier) {
      // Pay-as-you-go pricing
      const tierPrices = { '500': 15, '1000': 25 };
      price = tierPrices[contactTier] || 15;
    }

    // Add £5 for country targeting if specified
    if (targetCountry && targetCountry.trim() !== '' && targetCountry.toLowerCase() !== 'global') {
      price += 5;
    }
  
    const campaignData = {
      emailSubject: contentValidation.value.emailSubject,
      emailBody: contentValidation.value.emailBody,
      searchTerm: searchTermInput.trim(),
      gmailAccount: canUseAdvancedCampaignSending ? selectedGmailAccounts[0] : gmailAccount,
      contactTier,
      subscriptionPlan,
      amount: price,
      contentVariations: contentVariations.length > 0 ? contentVariations : [emailBody.trim()],
      targetCountry: targetCountry || "",
      userId: user.uid,
      ...(canUseAdvancedCampaignSending && advancedValidation?.valid ? {
        advancedSendingEnabled: true,
        gmailAccounts: advancedValidation.value.gmailAccounts,
        sendingWeekdays: advancedValidation.value.sendingWeekdays,
        sendingWindow: advancedValidation.value.sendingWindow
      } : {})
    };
  
    try {
      setLoading(true);
      const idToken = await user.getIdToken();
      
      const url = `${API_BASE_URL}/api/campaigns/start-campaign-checkout`;
      console.log("Creating campaign at", url);
      
      const res = await axios.post(
        url,
        campaignData,
        {
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        }
      );
  
      if (res.status === 200 && res.data.success) {
        const { campaignId, skipPayment, amount } = res.data.data;
        
        if (skipPayment) {
          // ✅ User has active subscription - skip payment!
          message.success(`Campaign created successfully! (${subscription?.plan || 'subscription'} plan)`);
          setShowModal(false);
          resetForm();
          
          // Clear cache
          if (user?.uid) {
            console.log('🗑️ [Subscription] Clearing cache for subscribed user');
            invalidateCampaigns(user.uid);
          }
          
          // Navigate back to campaigns page (no payment needed)
          setTimeout(() => {
            console.log('🚀 [Subscription] Navigating to campaigns (payment skipped)');
            navigate('/campaigns');
          }, 50);
          
        } else {
          // User needs to pay - redirect to payment page
          message.success("Campaign created successfully! Redirecting to payment...");
          setShowModal(false);
          resetForm();
          
          // ✅ MOBILE FIX: Clear cache directly (no event race condition)
          if (user?.uid) {
            console.log('🗑️ [MobileFix] Clearing cache directly before navigation');
            invalidateCampaigns(user.uid);
          }
          
          // ✅ MOBILE FIX: Small delay + React Router (ensures cache clears on mobile)
          setTimeout(() => {
            console.log('🚀 [MobileFix] Navigating to payment after cache clear');
            navigate(`/payment/${campaignId}`);
          }, 50); // 50ms ensures cache invalidation completes on mobile browsers
        }
      } else {
        throw new Error(res.data.error || "Campaign creation failed.");
      }
    } catch (err) {
      console.error("Campaign creation error", { status: err?.response?.status || null });
      if (err.response?.status === 401) {
        message.error("Authentication failed. Please log out and log back in.");
      } else if (err.response?.status === 403 && err.response?.data?.code === 'MONTHLY_LIMIT_REACHED') {
        // ✅ NEW: Handle monthly limit exceeded
        const details = err.response.data.details;
        const renewalDateStr = details.renewalDate?.seconds 
          ? new Date(details.renewalDate.seconds * 1000).toLocaleDateString()
          : 'next billing cycle';
        
        message.error({
          content: (
            <div>
              <strong>Monthly Limit Reached</strong>
              <p style={{ margin: '8px 0 0 0', fontSize: '12px' }}>
                You've used {details.currentUsage} of {details.monthlyLimit} contacts this month.
                {details.remainingContacts > 0 && ` (${details.remainingContacts} remaining)`}
              </p>
              <p style={{ margin: '4px 0 0 0', fontSize: '12px' }}>
                Your limit resets on {renewalDateStr}. Consider upgrading your plan for more contacts!
              </p>
            </div>
          ),
          duration: 10
        });
      } else if (err.response?.status === 400) {
        setError(err.response.data.error || "Invalid campaign data. Please check your inputs.");
      } else {
        setError("Failed to create campaign. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Are you sure you want to delete this campaign?")) return;
    
    try {
      setLoading(true);
      await deleteDoc(doc(db, "campaigns", id));
      setCampaigns((prev) => prev.filter((c) => c.id !== id));
      
      // PHASE 2B: Invalidate cache after deletion
      invalidateCampaigns(user?.uid);
      refetchCampaigns();
      
      message.success("Campaign deleted successfully!");
    } catch (err) {
      console.error("Error deleting campaign", { status: err?.response?.status || null });
      message.error("Failed to delete campaign");
    } finally {
      setLoading(false);
    }
  };

  const handleRunCampaign = async (campaignId) => {
    try {
      setLoading(true);
      const response = await axiosAuth.post(`/api/email-campaigns/run-campaign/${campaignId}`);
      
      if (response.data.success) {
        message.success("Campaign started successfully!");
        // Refresh campaigns list
        window.triggerCampaignRefresh('manual-run');
      } else {
        message.error(response.data.error || "Failed to start campaign");
      }
    } catch (error) {
      console.error("Error running campaign", { status: error?.response?.status || null });
      message.error(error.response?.data?.error || "Failed to start campaign");
    } finally {
      setLoading(false);
    }
  };

  const isGmailAuthPaused = (record) =>
    record?.paused && record?.pauseType === 'gmail_auth';

  const handleResumeGmailAuthCampaign = async (campaignId) => {
    try {
      setLoading(true);
      const response = await axiosAuth.post(`/api/email-campaigns/${campaignId}/resume`);
      if (response.data.success) {
        message.success('Campaign resume queued');
        window.triggerCampaignRefresh?.('gmail-auth-resume');
      } else {
        message.error(response.data.error || 'Failed to resume campaign');
      }
    } catch (error) {
      if (error.response?.status === 409) {
        message.warning(error.response?.data?.userMessage || 'Reconnect Gmail required');
        navigate('/manage-accounts');
      } else {
        message.error(error.response?.data?.error || 'Failed to resume campaign');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = async (campaign) => {
    let editableCampaign = campaign;
    try {
      const response = await axiosAuth.get(`/api/email-campaigns/${campaign.id}/detail`);
      editableCampaign = response.data.campaign;
    } catch (error) {
      message.error(error.response?.data?.error || "Failed to load campaign details");
      return;
    }
    const persistedSchedule = resolvePersistedCampaignSchedule(editableCampaign);
    setEmailSubject(editableCampaign.emailSubject || "");
    setSearchTermInput(editableCampaign.searchTerm || editableCampaign.hashtag || "");
    setEmailBody(editableCampaign.emailBody || "");
    setTargetCountry(editableCampaign.targetCountry || "");
    setGmailAccount(editableCampaign.gmailAccount || "");
    setSelectedGmailAccounts(
      Array.isArray(editableCampaign.gmailAccounts) && editableCampaign.gmailAccounts.length > 0
        ? editableCampaign.gmailAccounts
        : (editableCampaign.gmailAccount ? [editableCampaign.gmailAccount] : [])
    );
    setSendingWeekdays(persistedSchedule.sendingWeekdays);
    setSendingFullDay(persistedSchedule.sendingFullDay);
    setSendingStartTime(persistedSchedule.sendingStartTime);
    setSendingEndTime(persistedSchedule.sendingEndTime);
    setCampaignTimezone(persistedSchedule.campaignTimezone);
    setContactTier(editableCampaign.contactTier || "");
    setSubscriptionPlan(editableCampaign.subscriptionPlan || "");
    setContentVariations(editableCampaign.contentVariations || []);
    
    // Set editing state
    setIsEditing(true);
    setEditingCampaignId(editableCampaign.id);
    setEditingCampaign(editableCampaign);
    setError("");
    setShowModal(true);
  };

  const handleModalClose = () => {
    setShowModal(false);
    resetForm();
  };

  const handleCreateNew = () => {
    resetForm();
    setShowModal(true);
  };

  // Spintax preview functions using spintax-v2 engine
  const handleSpintaxPreview = () => {
    if (!emailBody.trim()) {
      setError("Please enter email body content first");
      return;
    }
    
    // Check if text already has spintax syntax
    const detection = detectSpintax(emailBody);
    
    let textToProcess = emailBody;
    
    // If no spintax syntax found, auto-convert plain text to spintax using synonymMap
    if (!detection.hasSpintax) {
      console.log('🌀 No spintax syntax detected - auto-converting plain text using synonymMap');
      textToProcess = convertToSpintax(emailBody);
      
      // Check if conversion added any spintax
      const conversionCheck = detectSpintax(textToProcess);
      if (!conversionCheck.hasSpintax) {
        message.info(
          "Tip: Use {option1|option2|option3} syntax for custom variations. Auto-generating variations from your text..."
        );
      } else {
        message.success(
          `Auto-generated ${conversionCheck.syntaxCount} spintax variation points from your text!`
        );
      }
    }
    
    // Generate 5 unique variations using spintax-v2
    const variations = generateSpintaxVariations(textToProcess, 5);
    
    // If we got fewer than 5 variations, it means the text has limited variation potential
    if (variations.length < 5) {
      message.warning(
        `Could only generate ${variations.length} unique variation(s). Add more {option1|option2} patterns for more variety.`
      );
    }
    
    setSpintaxPreview(variations);
    setShowSpintaxPreview(true);
    setError(""); // Clear any previous errors
  };

  // ✅ Gmail v2 - OAuth popup using centralized popup manager
  const handleAddGmailPopup = async () => {
    if (!user) {
      message.error("Please log in first");
      return;
    }

    const token = await user.getIdToken(true);
    localStorage.setItem("authToken", token);

    try {
      const response = await axiosAuth.post('/api/auth-v2/google');
      if (!response.data?.authorizationUrl) {
        throw new Error('Gmail authentication could not be started');
      }
      openAuthPopup(response.data.authorizationUrl, 'create-campaign');
    } catch {
      message.error('Failed to start Gmail authentication. Please try again.');
    }
    
    // ✅ MEO PATTERN: No popup error check - browser handles popup blockers naturally
  };

  const handleUseVariation = (variation) => {
    setEmailBody(variation);
    setContentVariations(prev => [...prev, variation]);
    setShowSpintaxPreview(false);
  };

  const handleUseSpintaxTemplate = () => {
    setContentVariations(prev => [...prev, emailBody]);
    setShowSpintaxPreview(false);
  };

  const filteredCampaigns = campaigns;

  const columns = [
    {
      title: "Search Term",
      dataIndex: "searchTerm",
      key: "searchTerm",
      render: (text) => (text ? text : "-")
    },
    {
      title: "Campaign Name",
      dataIndex: "name",
      key: "name",
      ellipsis: true,
      render: (text, record) => {
        // Fallback logic
        return record.name || record.emailSubject || record.searchTerm || 'Untitled Campaign';
      }
    },
    {
      title: "Country",
      dataIndex: "targetCountry",
      key: "targetCountry",
      render: (code) =>
        countryOptions.find((c) => c.code === code)?.name || "Global"
    },
    {
      title: "Recipients",
      dataIndex: "totalRecipients",
      key: "totalRecipients",
      width: 100,
      align: "center",
      render: (value, record) => {
        const count = value || record.totalRecipients || 0;
        
        // If recipients exist, just show the count
        if (count > 0) {
          return <span style={{ fontWeight: 500 }}>{count.toLocaleString()}</span>;
        }
        
        // If no recipients, show neutral waiting message (matches MEO exactly)
        return (
          <span style={{ color: '#faad14' }}>
            ⏳ Waiting for recipients...
          </span>
        );
      }
    },
    {
      title: "Progress",
      key: "progress",
      width: 150,
      render: (_, record) => (
        <ProgressInfo 
          totalSent={record.sentCount || record.emailsSent || 0} 
          totalRecipients={record.totalRecipients || 0}
        />
      )
    },
    {
      title: "New Daily Quota",
      key: "dailyQuota",
      width: 180,
      render: (_, record) => (
        <NewDailyQuota 
          dailyEmailsSent={record.dailyEmailsSent || 0} 
          gmailAccount={record.gmailAccount || 'N/A'} 
        />
      )
    },
    {
      title: "Status",
      dataIndex: "status",
      key: "status",
      render: (status, record) => {
        if (status === "running" && record.paused) {
          const pendingCount = record.pendingRecipients?.length || 0;
          const sentCount = record.sentCount || record.emailsSent || 0;
          const totalCount = record.totalRecipients || 0;
          const progress = record.progress || `${sentCount}/${totalCount}`;
          const isAuthPause = isGmailAuthPaused(record);
          return (
            <div>
              <span style={{ color: '#ff4d4f' }}>⏸️ Paused</span>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {isAuthPause
                  ? (record.pauseReason || 'Reconnect Gmail required')
                  : (record.pauseReason || 'Daily quota exceeded')}
              </div>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {progress} sent ({pendingCount} pending)
              </div>
            </div>
          );
        } else if (status === "running" && !record.paused) {
          const pendingCount = record.pendingRecipients?.length || 0;
          const sentCount = record.sentCount || record.emailsSent || 0;
          const totalCount = record.totalRecipients || 0;
          const progress = record.progress || `${sentCount}/${totalCount}`;
          return (
            <div>
              <span style={{ color: '#1890ff' }}>🤖 Auto-continuing</span>
              <div style={{ fontSize: '12px', color: '#666' }}>
                {progress} sent ({pendingCount} pending)
              </div>
            </div>
          );
        } else if (status === "completed") {
          return <span style={{ color: '#52c41a' }}>✅ Completed</span>;
        } else if (status === "pending") {
          const recipientCount = record.totalRecipients || 0;
          if (recipientCount > 0) {
            return <span style={{ color: '#1890ff' }}>🚀 Pending</span>;
          } else {
            return <span style={{ color: '#faad14' }}>⏳ Awaiting Recipients</span>;
          }
        } else if (status === "draft") {
          return <span style={{ color: '#faad14' }}>📝 Draft</span>;
        }
        return status;
      },
    },
    {
      title: "Payment Status",
      dataIndex: "paymentStatus",
      key: "paymentStatus",
      render: (paymentStatus) => (
        <span className={`payment-status-${paymentStatus}`}>
          {paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
        </span>
      ),
    },
    {
      title: "Date",
      dataIndex: "createdAt",
      key: "createdAt",
      align: "center",
      render: (date) => {
        // FIXED: Handle both Firestore timestamps and regular dates
        if (!date) return "-";
        
        let dateObj;
        if (date && typeof date === 'object' && date.toDate) {
          // Firestore timestamp
          dateObj = date.toDate();
        } else if (date instanceof Date) {
          // Already a Date object
          dateObj = date;
        } else {
          // String or number
          dateObj = new Date(date);
        }
        
        // Check if date is valid
        if (isNaN(dateObj.getTime())) {
          return "-";
        }
        
        return dateObj.toLocaleDateString();
      }
    },
    {
      title: "Actions",
      key: "actions",
      render: (_, record) => (
        <Space>
          {record.status === "running" && record.paused ? (
            <span style={{ color: '#ff4d4f', fontStyle: 'italic' }}>
              ⏸️ Paused - Will resume automatically
            </span>
          ) : record.status === "running" && !record.paused ? (
            <span style={{ color: '#1890ff', fontStyle: 'italic' }}>
              🤖 Auto-continuing daily
            </span>
          ) : record.status === "completed" ? (
            <span style={{ color: '#52c41a', fontStyle: 'italic' }}>
              ✅ Completed
            </span>
          ) : (
            <Button
              type="primary"
              onClick={() => {
                console.log("🚀 Run Campaign requested", {
                  campaignId: record.id,
                  status: record.status,
                  paymentStatus: record.paymentStatus,
                  recipientCount: Number(record.totalRecipients || 0),
                  totalRecipients: record.totalRecipients,
                  hasRecipients: record.hasRecipients
                });
                handleRunCampaign(record.id);
              }}
              disabled={
                record.status !== "draft" || 
                record.paymentStatus !== "paid" || 
                !record.hasRecipients
              }
              title={
                record.status !== "draft" ? `Only draft campaigns can be run manually (current: ${record.status})` :
                record.paymentStatus !== "paid" ? `Payment required first (current: ${record.paymentStatus})` :
                !record.hasRecipients ? "No recipients available - add recipients first" :
                "Run campaign manually (debug mode only)"
              }
            >
              Run Campaign (Debug)
            </Button>
          )}
          {record.paymentStatus === "unpaid" && record.status !== "pending" && (
            <Button
              onClick={() => {
                // Calculate amount for display (though not used in navigation)
                const subscriptionPrices = { 'starter': 99, 'growth': 299, 'scale': 599 };
                const tierPrices = { '500': 15, '1000': 25 };
                let amount = record.amount || subscriptionPrices[record.subscriptionPlan] || tierPrices[record.contactTier] || 15;

                // ⚠️ Add country targeting add-on ONLY for PAYG, NOT for subscriptions
                const isSubscription = !!subscriptionPrices[record.subscriptionPlan];
                if (record.targetCountry && record.targetCountry.trim() !== '' && record.targetCountry.toLowerCase() !== 'global' && !isSubscription) {
                  amount += 10;
                  console.log('🌍 PAYG country-targeting add-on applied');
                }
                console.log(`💰 Payment amount calculated: £${amount}`);
                navigate(`/payment/${record.id}`);
              }}
            >
              Pay Now
            </Button>
          )}

          <Button
            type="default"
            onClick={() => handleEdit(record)}
          >
            Details
          </Button>
          <Button
            type="primary"
            danger
            onClick={() => handleDelete(record.id)}
            disabled={record.status === "running"}
          >
            Delete
          </Button>
        </Space>
      ),
    }
  ];

  return (
    <>
      <Sidebar />
      <div className="campaigns-layout">
        <div className="campaigns-main">
          <div className="campaigns-header">
            <h1>Email Campaigns</h1>
            <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
              {isSubscribed ? (
                <Button
                  type="primary"
                  className="subscribe-btn"
                  disabled
                  style={{
                    cursor: 'not-allowed',
                    height: '40px'
                  }}
                >
                  ✓ Subscribed
                </Button>
              ) : (
                <Button
                  type="primary"
                  className="subscribe-btn"
                  onClick={() => navigate('/subscribe')}
                  style={{ height: '40px' }}
                >
                  Subscribe Now!
                </Button>
              )}
              <Button
                type="primary"
                onClick={handleCreateNew}
                icon={<MailOutlined />}
                className="create-campaign-btn"
                style={{ height: '40px' }}
              >
                Automate Emails
              </Button>
            </div>
          </div>

        {loading ? (
          <div style={{ textAlign: "center", padding: "2rem" }}>
            <Spin size="large" />
          </div>
        ) : filteredCampaigns.length > 0 ? (
          <>
            {/* Desktop Table View */}
            <div className="campaigns-table-desktop">
              <Table
                columns={columns}
                dataSource={filteredCampaigns}
                rowKey="id"
                scroll={{ x: 'max-content' }}
                pagination={{
                  pageSize: 10,
                  showSizeChanger: true,
                  showQuickJumper: true,
                  showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} campaigns`,
                }}
                locale={{ emptyText: "No campaigns found" }}
              />
            </div>

            {/* Mobile Card View */}
            <div className="campaigns-cards-mobile">
              {filteredCampaigns.map((campaign) => {
                const recipientCount = campaign.totalRecipients || 0;
                const sentCount = campaign.sentCount || campaign.emailsSent || 0;
                const pendingCount = campaign.pendingRecipients?.length || 0;
                const dailyEmailsSent = campaign.dailyEmailsSent || 0;
                const countryName = countryOptions.find((c) => c.code === campaign.targetCountry)?.name || "Global";
                
                let dateObj;
                if (campaign.createdAt && typeof campaign.createdAt === 'object' && campaign.createdAt.toDate) {
                  dateObj = campaign.createdAt.toDate();
                } else if (campaign.createdAt instanceof Date) {
                  dateObj = campaign.createdAt;
                } else if (campaign.createdAt) {
                  dateObj = new Date(campaign.createdAt);
                }
                const formattedDate = dateObj && !isNaN(dateObj.getTime()) ? dateObj.toLocaleDateString() : "-";

                return (
                  <div key={campaign.id} className="campaign-card">
                    <div className="card-header">
                      <h3>{campaign.name || campaign.emailSubject || campaign.searchTerm || 'Untitled Campaign'}</h3>
                      <span className={`payment-status-${campaign.paymentStatus}`}>
                        {campaign.paymentStatus === 'paid' ? 'Paid' : 'Unpaid'}
                      </span>
                    </div>

                    <div className="card-info">
                      <div className="info-row">
                        <span className="label">Search Term:</span>
                        <span className="value">{campaign.searchTerm || "-"}</span>
                      </div>

                      <div className="info-row">
                        <span className="label">Country:</span>
                        <span className="value">{countryName}</span>
                      </div>

                      <div className="info-row">
                        <span className="label">Recipients:</span>
                        <span className="value">
                          {recipientCount > 0 ? (
                            <span style={{ fontWeight: 500 }}>{recipientCount.toLocaleString()}</span>
                          ) : (
                            <span style={{ color: '#faad14' }}>⏳ Waiting for recipients...</span>
                          )}
                        </span>
                      </div>

                      <div className="info-row">
                        <span className="label">Progress:</span>
                        <span className="value">
                          <ProgressInfo 
                            totalSent={sentCount} 
                            totalRecipients={recipientCount} 
                          />
                        </span>
                      </div>

                      <div className="info-row">
                        <span className="label">Daily Quota:</span>
                        <span className="value">
                          <NewDailyQuota 
                            dailyEmailsSent={dailyEmailsSent} 
                            gmailAccount={campaign.gmailAccount || 'N/A'} 
                          />
                        </span>
                      </div>

                      <div className="info-row">
                        <span className="label">Status:</span>
                        <span className="value">
                          {campaign.status === "running" && campaign.paused ? (
                            <div>
                              <span style={{ color: '#ff4d4f' }}>⏸️ Paused</span>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {isGmailAuthPaused(campaign)
                                  ? (campaign.pauseReason || 'Reconnect Gmail required')
                                  : (campaign.pauseReason || 'Daily quota exceeded')}
                              </div>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {sentCount}/{recipientCount} sent ({pendingCount} pending)
                              </div>
                            </div>
                          ) : campaign.status === "running" && !campaign.paused ? (
                            <div>
                              <span style={{ color: '#1890ff' }}>🤖 Auto-continuing</span>
                              <div style={{ fontSize: '12px', color: '#666' }}>
                                {sentCount}/{recipientCount} sent ({pendingCount} pending)
                              </div>
                            </div>
                          ) : campaign.status === "completed" ? (
                            <span style={{ color: '#52c41a' }}>✅ Completed</span>
                          ) : campaign.status === "pending" ? (
                            recipientCount > 0 ? (
                              <span style={{ color: '#1890ff' }}>🚀 Pending</span>
                            ) : (
                              <span style={{ color: '#faad14' }}>⏳ Awaiting Recipients</span>
                            )
                          ) : campaign.status === "draft" ? (
                            <span style={{ color: '#faad14' }}>📝 Draft</span>
                          ) : (
                            campaign.status
                          )}
                        </span>
                      </div>

                      <div className="info-row">
                        <span className="label">Created:</span>
                        <span className="value">{formattedDate}</span>
                      </div>
                    </div>

                    <div className="card-actions">
                      {campaign.status === "running" && campaign.paused ? (
                        isGmailAuthPaused(campaign) ? (
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
                            <span style={{ color: '#ff4d4f', fontStyle: 'italic', fontSize: '0.85rem' }}>
                              Gmail reconnect required
                            </span>
                            <Button size="small" onClick={handleAddGmailPopup}>
                              Reconnect Gmail
                            </Button>
                            <Button size="small" type="primary" onClick={() => handleResumeGmailAuthCampaign(campaign.id)}>
                              Resume Campaign
                            </Button>
                          </div>
                        ) : (
                          <span style={{ color: '#ff4d4f', fontStyle: 'italic', fontSize: '0.85rem' }}>
                            ⏸️ Paused - Will resume automatically
                          </span>
                        )
                      ) : campaign.status === "running" && !campaign.paused ? (
                        <span style={{ color: '#1890ff', fontStyle: 'italic', fontSize: '0.85rem' }}>
                          🤖 Auto-continuing daily
                        </span>
                      ) : campaign.status === "completed" ? (
                        <span style={{ color: '#52c41a', fontStyle: 'italic', fontSize: '0.85rem' }}>
                          ✅ Completed
                        </span>
                      ) : (
                        <Button
                          type="primary"
                          onClick={() => handleRunCampaign(campaign.id)}
                          disabled={
                            campaign.status !== "draft" || 
                            campaign.paymentStatus !== "paid" || 
                            recipientCount === 0
                          }
                          block
                        >
                          Run Campaign (Debug)
                        </Button>
                      )}
                      <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.75rem' }}>
                        <Button 
                          onClick={() => handleEdit(campaign)}
                          block
                        >
                          View Details
                        </Button>
                        {campaign.paymentStatus === 'unpaid' && (
                          <Button
                            type="primary"
                            onClick={() => window.location.href = `/payment/${campaign.id}`}
                            block
                          >
                            Pay Now
                          </Button>
                        )}
                        <Button
                          danger
                          onClick={() => handleDelete(campaign.id)}
                          disabled={campaign.status === 'running'}
                          block
                        >
                          Delete
                        </Button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </>
        ) : (
          <div className="empty-state">
            <div className="empty-state-icon">
              <MailOutlined />
            </div>
            <h3>No campaigns found</h3>
            <p>Create your first email campaign to get started</p>
            <Button type="primary" onClick={handleCreateNew}>
              Automate Emails
            </Button>
          </div>
        )}

      {/* Campaign Creation/Edit Modal */}
      <Modal
        title={
          isEditing && editingCampaign?.advancedSendingEnabled === true && canUseAdvancedCampaignSending
            ? "Campaign Details & Advanced Sending"
            : (isEditing ? "View Campaign Details" : "Automate New Emails")
        }
        open={showModal}
        onCancel={handleModalClose}
        footer={null}
        width={800}
        wrapClassName="campaign-modal-wrap"
        destroyOnClose={true}
      >
        <form onSubmit={handleSubmit}>
          {/* ✅ Active subscription banner (matches MEO ordering) */}
          {isSubscribed && !subscriptionLoading && subscription && (
            <div style={{
              backgroundColor: '#f6ffed',
              border: '1px solid #b7eb8f',
              borderRadius: '8px',
              padding: '16px',
              marginBottom: '24px',
              width: '100%',
              boxSizing: 'border-box'
            }}>
              <div style={{ fontWeight: 600, color: '#389e0d', marginBottom: '4px', width: '100%' }}>
                ✓ Active Subscription: {subscription.plan?.charAt(0).toUpperCase() + subscription.plan?.slice(1).toLowerCase()} Plan
              </div>
              <div style={{ fontSize: '12px', color: '#595959', marginBottom: '12px', width: '100%' }}>
                No payment required for this campaign
              </div>
              <div style={{ fontSize: '11px', color: '#595959', width: '100%', display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                <span>· {(subscription.monthlyUsage || 0).toLocaleString()} / {(subscription.monthlyLimit || 0).toLocaleString()}</span>
                <span>· {(subscription.remainingContacts || 0).toLocaleString()} Contacts Remaining</span>
                {subscription.resetDateMillis && (
                  <span>· Resets: {new Date(subscription.resetDateMillis).toLocaleDateString()}</span>
                )}
              </div>
            </div>
          )}
          {/* ✅ FIX 2: MEO Field Order - 1. Contact Tier */}
          <div className="form-group">
            <label>
              <span className="field-label"><span style={{ color: "red" }}>*</span> Contact Tier</span>
              <Select
                value={contactTier}
                onChange={(value) => {
                  if (isSubscribed) {
                    return;
                  }
                  setContactTier(value);
                  setSubscriptionPlan("");
                }}
                style={{ width: '100%' }}
                disabled={isEditing || isSubscribed}
                placeholder={
                  isSubscribed
                    ? "Unlimited plan active (tier selection disabled)"
                    : "Select contact tier"
                }
              >
                <Select.Option value="500">Up to 500 Contacts (£15)</Select.Option>
                <Select.Option value="1000">Up to 1,000 Contacts (£25)</Select.Option>
              </Select>
            </label>
          </div>

          {/* ✅ FIX 2: MEO Field Order - 2. Subscription Plan */}
          <div className="form-group">
            <label>
              <span className="field-label">Subscription Plan (Optional)</span>
              <Select
                value={subscriptionSelectValue}
                onChange={(value) => {
                  if (isSubscribed) {
                    return;
                  }
                  // ✅ MEO-style redirect: When selecting subscription plan, navigate to /subscribe
                  if (value === 'starter' || value === 'growth' || value === 'scale') {
                    message.info("Redirecting to subscription page...");
                    window.open(`/subscribe?plan=${value}`, '_blank');
                    handleModalClose(); // Close the modal
                  } else {
                    setSubscriptionPlan(value);
                    setContactTier("");
                  }
                }}
                style={{ width: '100%' }}
                disabled={isEditing || isSubscribed}
                placeholder={
                  isSubscribed
                    ? "Already Subscribed"
                    : "Select subscription plan (overrides contact tier)"
                }
                dropdownRender={(menu) =>
                  isSubscribed ? (
                    <div style={{ padding: '8px 12px', color: '#8c8c8c', fontWeight: 500 }}>
                      Already Subscribed
                    </div>
                  ) : (
                    menu
                  )
                }
              >
                {isSubscribed && (
                  <Select.Option value="already_subscribed">
                    Already Subscribed
                  </Select.Option>
                )}
                <Select.Option value="starter">Starter Plan - £99/month (up to 5,000 contacts)</Select.Option>
                <Select.Option value="growth">Growth Plan - £299/month (up to 15,000 contacts)</Select.Option>
                <Select.Option value="scale">Scale Plan - £599/month (up to 30,000 contacts)</Select.Option>
              </Select>
            </label>
          </div>

          {/* ✅ FIX 2: MEO Field Order - 3. Search Term */}
          <div className="form-group">
            <label>
              <span className="field-label"><span style={{ color: "red" }}>*</span> Search Term</span>
              <Input
                required
                value={searchTermInput}
                onChange={(e) => setSearchTermInput(e.target.value)}
                placeholder="Influencer, Developer, Restaurant, Musician, Trader, Actor etc"
                disabled={isEditing}
              />
            </label>
          </div>

          {canUseAdvancedCampaignSending
            && (!isEditing || editingCampaign?.advancedSendingEnabled === true) ? (
            <AdvancedCampaignSettings
              gmailAccounts={gmailAccounts}
              selectedGmailAccounts={selectedGmailAccounts}
              onSelectedGmailAccountsChange={(accounts) => {
                setSelectedGmailAccounts(accounts);
                setGmailAccount(accounts[0] || "");
              }}
              onAddGmailAccount={handleAddGmailPopup}
              sendingWeekdays={sendingWeekdays}
              onSendingWeekdaysChange={setSendingWeekdays}
              sendingFullDay={sendingFullDay}
              onSendingFullDayChange={setSendingFullDay}
              sendingStartTime={sendingStartTime}
              onSendingStartTimeChange={setSendingStartTime}
              sendingEndTime={sendingEndTime}
              onSendingEndTimeChange={setSendingEndTime}
              campaignTimezone={campaignTimezone}
              onCampaignTimezoneChange={setCampaignTimezone}
            />
          ) : (
            <div className="form-group">
              <label>
                <span className="field-label"><span style={{ color: "red" }}>*</span> Gmail Account</span>
                <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                  <Select
                    required
                    value={gmailAccount}
                    onChange={(value) => setGmailAccount(value)}
                    style={{ flex: 1 }}
                    disabled={isEditing}
                    placeholder={gmailAccounts.length === 0 ? "No Gmail accounts available" : "Select Gmail Account"}
                    virtual={false}
                    listHeight={250}
                    showSearch
                    filterOption={(input, option) =>
                      (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                    }
                    options={gmailAccounts.map((account) => ({
                      value: account.email,
                      label: account.email
                    }))}
                  />
                  <Button
                    type="default"
                    onClick={handleAddGmailPopup}
                    icon={<span>+</span>}
                  >
                    Add Account
                  </Button>
                </div>
                {gmailAccounts.length === 0 && (
                  <p style={{ color: "orange", fontSize: "0.9rem", marginTop: 4 }}>
                    No Gmail accounts found. Click the button above to add one.
                  </p>
                )}
              </label>
            </div>
          )}

          {/* ✅ FIX 2: MEO Field Order - 5. Target Country */}
          <div className="form-group">
            <label>
              <span className="field-label">Target Country (optional)</span>
              <Select
                value={targetCountry}
                onChange={(value) => setTargetCountry(value)}
                style={{ width: '100%' }}
                disabled={isEditing}
                virtual={false}
                listHeight={300}
                showSearch
                allowClear
                placeholder="Global (All Countries)"
                filterOption={(input, option) =>
                  (option?.label ?? '').toLowerCase().includes(input.toLowerCase())
                }
                options={countryOptions.map((country) => ({
                  value: country.code,
                  label: country.name
                }))}
              />
            </label>
          </div>

          {isEditing
            && canUseAdvancedCampaignSending
            && editingCampaign?.advancedSendingEnabled === true ? (
            <AdvancedCampaignContentFields
              emailSubject={emailSubject}
              emailBody={emailBody}
              persistedEmailBody={editingCampaign?.emailBody}
              hasStoredVariations={
                Array.isArray(editingCampaign?.contentVariations)
                && editingCampaign.contentVariations.length > 0
              }
              onEmailSubjectChange={setEmailSubject}
              onEmailBodyChange={setEmailBody}
              terminalCampaign={['completed', 'cancelled', 'canceled'].includes(
                String(editingCampaign?.status || '').toLowerCase()
              )}
            />
          ) : (
            <>
              <div className="form-group">
                <label>
                  <span className="field-label"><span style={{ color: "red" }}>*</span> Email Subject</span>
                  <Input
                    required
                    value={emailSubject}
                    onChange={(e) => setEmailSubject(e.target.value)}
                    placeholder="Enter email subject"
                    disabled={isEditing}
                  />
                </label>
              </div>
              <div className="form-group">
                <label>
                  <span className="field-label"><span style={{ color: "red" }}>*</span> Email Content</span>
                  <TextArea
                    required
                    rows={4}
                    value={emailBody}
                    onChange={(e) => setEmailBody(e.target.value)}
                    placeholder="Enter email content"
                    disabled={isEditing}
                  />
                </label>
              </div>
            </>
          )}

          {/* ✅ Recipients - same height as Email Content */}
          {/* TARGETED PATCH: Show actual recipients when viewing existing campaign details */}
          <div className="form-group">
            <label>
              <span className="field-label">Recipients</span>
              <TextArea
                rows={4}
                placeholder={isEditing ? "No recipients found for this campaign" : "These contacts will appear here after payment..."}
                disabled
                value={isEditing && editingCampaign?.recipients && Array.isArray(editingCampaign.recipients) && editingCampaign.recipients.length > 0
                  ? editingCampaign.recipients.map(r => typeof r === 'string' ? r : r.email || r.contact || 'Unknown').filter(Boolean).join('\n')
                  : ""
                }
              />
              {isEditing && editingCampaign && (
                <div style={{ marginTop: '8px', fontSize: '12px', color: '#666' }}>
                  Total Recipients: {editingCampaign.totalRecipients || editingCampaign.recipients?.length || 0}
                  {editingCampaign.recipients?.length > 0 && (
                    <> • Showing {editingCampaign.recipients.length} email addresses</>
                  )}
                </div>
              )}
            </label>
          </div>

          {error && (
            <div style={{ color: "red", marginBottom: 16, padding: 8, backgroundColor: "#fff2f0", border: "1px solid #ffccc7", borderRadius: 4 }}>
              {error}
            </div>
          )}

          {/* ✅ FIX 2: MEO-style horizontal button layout */}
          {!isEditing ? (
            <Space style={{ marginTop: 16 }}>
              <Button 
                type="default" 
                onClick={handleSpintaxPreview}
              >
                Preview Spintax Variations
              </Button>
              <Button 
                type="primary" 
                htmlType="submit" 
                loading={loading}
              >
                Automate Emails
              </Button>
            </Space>
          ) : (
            <div style={{ marginTop: 16, textAlign: 'right' }}>
              {canUseAdvancedCampaignSending && editingCampaign?.advancedSendingEnabled === true && (
                <Button
                  type="primary"
                  htmlType="submit"
                  loading={loading}
                  disabled={loading}
                  style={{ marginRight: 8 }}
                >
                  Save changes
                </Button>
              )}
              <Button onClick={handleModalClose}>
                Discard changes
              </Button>
            </div>
          )}
        </form>
      </Modal>

      {/* Spintax Preview Modal */}
      {showSpintaxPreview && (
        <Modal
          title="Spintax Preview"
          open={showSpintaxPreview}
          onCancel={() => setShowSpintaxPreview(false)}
          footer={null}
          width={700}
        >
          <div style={{ marginBottom: 16 }}>
            <p style={{ color: "#666", marginBottom: 16 }}>
              <strong>Two options:</strong><br />
              1. Click any variation below to use that specific version<br />
              2. Or click "Use Spintax Template" to get random variations for each recipient
            </p>
          </div>

          <div style={{ maxHeight: 400, overflowY: 'auto' }}>
            {spintaxPreview.map((variation, i) => (
              <div key={i} style={{ 
                background: "#f9fafb", 
                padding: 12, 
                borderRadius: 8, 
                marginBottom: 12, 
                border: "1px solid #e5e7eb",
                display: "flex",
                justifyContent: "space-between",
                alignItems: "center",
                gap: 10
              }}>
                <div style={{ flex: 1 }}>
                  <strong>Variation {i + 1}:</strong> {variation}
                </div>
                <Button 
                  size="small"
                  onClick={() => handleUseVariation(variation)}
                >
                  Use This
                </Button>
              </div>
            ))}
          </div>

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
            <Button onClick={() => setShowSpintaxPreview(false)}>
              Cancel
            </Button>
            <Button type="primary" onClick={handleUseSpintaxTemplate}>
              Use Spintax Template
            </Button>
          </div>
        </Modal>
      )}
      </div>
    </div>
    </>
  );
};

export default CreateCampaign;
